import { useQuery, useMutation, useQueryClient } from '@tanstack/vue-query';
import { request } from '@/api/request';
import {
  mergeMutationOptions,
  mergeQueryOptions,
  type QueryOptions,
  type MutationOptions,
} from '@/modules/query/utils';
import { isAuthenticatedByRLRef } from '@/modules/auth/state';
import type { User, MFAPreference } from '@@/packages/shared/types/models/user';
import { COMMUNITY_POSTS_QUERY } from '@/modules/communities-message-board/api/queryKeys';
import type {
  GetCommunityPostResponse,
  GetCommunityPostsResponse,
} from '@/modules/communities-message-board/api';
import { EngagementAssociatedType } from '@@/packages/shared/types/models/engagements';
import { CURRENT_USER_QUERY } from './queryKeys';

export {
  fetchCurrentUserPermissions,
  useCurrentUserPermissions,
  type UserPermissionsResponse,
} from './permissions';

export const fetchCurrentUser = () => request<User>('GET', '/user');

type UpdateUserVariables = Partial<
  Pick<
    User,
    'firstName' | 'lastName' | 'phone' | 'language' | 'team' | 'jobTitle' | 'marketingEmails'
  >
>;

export const getUserMFAPreference = () =>
  request<{ mfaPreference: MFAPreference }>('GET', '/user/mfa-preference');

export const setNotificationPreference = (preference: any) =>
  request('PUT', '/notifications/preference', { preference });

export function useCurrentUser(options?: QueryOptions) {
  const query = useQuery(
    mergeQueryOptions(options, {
      queryKey: [CURRENT_USER_QUERY],
      async queryFn() {
        const res = await fetchCurrentUser();
        return res.data;
      },
      staleTime: Infinity,
      enabled: isAuthenticatedByRLRef,
    }),
  );

  const user = computed(() => query.data.value);

  const fullName = computed(() => user.value && `${user.value.firstName} ${user.value.lastName}`);
  const engagementsByType = computed(() => {
    if (!user.value?.engagements) return {};

    const userEngagements = user.value.engagements.filter(
      (engagement) => engagement.associatedType === EngagementAssociatedType.User,
    );

    return Object.fromEntries(userEngagements.map((e) => [e.engagementType, e]));
  });
  const currentUserID = computed(() => user.value?.userID);

  return { ...query, user, fullName, currentUserID, engagementsByType };
}

export function useUpdateCurrentUser(options?: MutationOptions<User, UpdateUserVariables>) {
  const queryClient = useQueryClient();

  return useMutation(
    mergeMutationOptions(options, {
      async mutationFn(user) {
        const res = await request<User>('PATCH', '/user', user);
        return res.data;
      },
      onSuccess(data) {
        queryClient.setQueryData([CURRENT_USER_QUERY], data);

        queryClient.setQueriesData(
          { queryKey: [COMMUNITY_POSTS_QUERY] },
          (oldData: GetCommunityPostsResponse | GetCommunityPostResponse | undefined) => {
            if (!oldData) return undefined;
            // This can either be post or posts response
            return {
              ...oldData,
              users:
                data.userID in oldData.users
                  ? {
                      ...oldData.users,
                      [data.userID]: {
                        ...oldData.users[data.userID],
                        ...data,
                      },
                    }
                  : oldData.users,
            } satisfies typeof oldData;
          },
        );
      },
    }),
  );
}
